/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ChangeEvent, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import * as yup from "yup";
import {
  Box,
  Button,
  Description,
  FlexContainer,
  Heading,
  Input,
  Label,
  Sidebar,
  Text,
  theme,
} from "@nordcloud/gnui";
import { Fieldset } from "~/components/Fieldset";
import { GlobalSpinner } from "~/components/Loaders";
import { useDisclosure } from "~/hooks";
import { useAddPerson } from "~/hooks/graphql";
import { LoaderWrap } from "../Utils";

const schema = yup.object().shape({
  name: yup.string().required(),
  email: yup.string().email().required(),
});

type PersonData = {
  id: string;
  name: string;
  email: string;
};

type ContactsSelectorFormData = {
  name: string;
  email: string;
};

type Props = {
  onClose: () => void;
  isOpen: boolean;
  options?: PersonData[];
  onSubmit: (id: string) => void;
  peopleLoading: boolean;
};

export function ContactSelector({
  onClose,
  isOpen,
  onSubmit,
  options = [],
  peopleLoading,
}: Props) {
  const [filter, setFilter] = useState("");
  const [people, setPeople] = useState<PersonData[]>(options);
  const [filteredPeople, setFilteredPeople] = useState<PersonData[]>(options);

  useEffect(() => {
    setPeople(options);
    setFilteredPeople(options);
  }, [options, isOpen]);

  const {
    isOpen: isAddPersonSidebarOpen,
    close: closeAddPersonSidebar,
    open: openAddPersonSidebar,
  } = useDisclosure();
  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm<ContactsSelectorFormData>({
    resolver: yupResolver(schema),
  });

  const filterPeople = (event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value.toLowerCase());
  };

  const [addPerson, { loading: isAddPersonLoading }] = useAddPerson();

  const onAddPersonSubmit = async (values: ContactsSelectorFormData) => {
    await addPerson(values, () => {
      closeAddPersonSidebar();
    });
  };

  const handleOpenAddPersonSidebar = () => {
    setFilter("");
    openAddPersonSidebar();
  };

  useEffect(() => {
    setFilteredPeople(
      people.filter(({ name }) => name.toLowerCase().includes(filter))
    );
  }, [people, filter]);

  return (
    <>
      <GlobalSpinner loading={isAddPersonLoading} />
      <Sidebar isOpen={isOpen} title="Select contact person" onClick={onClose}>
        <Box
          boxStyle="lightGrey"
          mb={theme.spacing.spacing04}
          innerSpacing="spacing02"
        >
          <FlexContainer>
            <Input
              small
              type="text"
              placeholder="Search"
              value={filter}
              onChange={filterPeople}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
            />
            <Button
              severity="low"
              size="md"
              icon="plus"
              ml={theme.spacing.spacing04}
              type="button"
              onClick={handleOpenAddPersonSidebar}
            >
              Add Employee
            </Button>
          </FlexContainer>
        </Box>
        <LoaderWrap inContent loading={peopleLoading}>
          {filteredPeople?.map((person: PersonData) => (
            <SidebarItemContainer
              key={person.id}
              onClick={() => onSubmit(person.id)}
            >
              <Heading level={4}>{person.name}</Heading>
              <Text size="sm" tag="div" color={theme.color.text.text02}>
                {person.email}
              </Text>
            </SidebarItemContainer>
          ))}
        </LoaderWrap>
      </Sidebar>
      <Sidebar
        title="Create new Contact Person"
        isOpen={isAddPersonSidebarOpen}
        onClick={closeAddPersonSidebar}
      >
        <form
          // Workaround for issue with React 17 event handling and nested forms https://github.com/facebook/react/issues/20741#issuecomment-870776352
          onSubmitCapture={handleSubmit(onAddPersonSubmit)}
        >
          <Fieldset>
            <Label name="Name" />
            <Input
              type="text"
              {...register("name")}
              placeholder="Name"
              status={errors.name ? "danger" : undefined}
            />
            {errors.name && <Description>Name is required.</Description>}
          </Fieldset>
          <Fieldset>
            <Label name="Email" />
            <Input
              type="text"
              {...register("email")}
              placeholder="Email"
              status={errors.name ? "danger" : undefined}
            />
            {errors.email && <Description>Email is required.</Description>}
          </Fieldset>
          <FlexContainer justifyContent="space-between">
            <Button
              severity="low"
              type="button"
              mt={theme.spacing.spacing04}
              onClick={closeAddPersonSidebar}
            >
              Cancel
            </Button>
            <Button type="submit" icon="user" mt={theme.spacing.spacing04}>
              Create New Contact Person
            </Button>
          </FlexContainer>
        </form>
      </Sidebar>
    </>
  );
}

const SidebarItemContainer = styled.div`
  padding: ${theme.spacing.spacing02};
  border-bottom: 1px solid ${theme.color.border.border01};
  text-align: left;

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.background.ui03};
  }
`;
